import { useEffect, useState } from "react";
import { api } from "../lib/api.ts";
import { useBrands } from "../lib/brand.tsx";
import { Icon } from "../lib/icons.tsx";
import { STANCE_META, type SecondOpinionOutput } from "@shared/opinion.ts";

const SAMPLES = [
  "ผมจะปิด Ad Set B แล้วโยกงบไป A",
  "CPL เริ่มถูกลง ผมว่าจะเพิ่มงบเป็น 2 เท่าเลย",
  "ผมจะเปลี่ยน Creative + Audience + Offer ใหม่หมดพร้อมกัน",
  "ตัวนี้ยิงมา 1 วันยังไม่มี Lead ผมว่าจะปิดทิ้ง",
];
const QUICK = [
  { key: "spend", label: "ใช้จ่าย ฿" },
  { key: "cpl", label: "CPL ฿" },
  { key: "lead", label: "Lead" },
  { key: "sale", label: "ยอดขาย" },
  { key: "roas", label: "ROAS" },
];

export default function SecondOpinion() {
  const { activeBrand } = useBrands();
  const [plan, setPlan] = useState("");
  const [metrics, setMetrics] = useState<Record<string, string>>({});
  const [out, setOut] = useState<SecondOpinionOutput | null>(null);
  const [isMock, setIsMock] = useState(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [saved, setSaved] = useState(false);

  useEffect(() => { load(); }, [activeBrand?.id]);
  async function load() {
    if (!activeBrand) return setHistory([]);
    const r = await api.get<{ opinions: any[] }>(`/opinion?brandId=${activeBrand.id}`);
    setHistory(r.opinions);
  }

  async function ask(text?: string) {
    const p = (text ?? plan).trim();
    if (!p) { setErr("พิมพ์แผนของคุณก่อน"); return; }
    if (text) setPlan(text);
    if (!activeBrand) { setErr("เลือกแบรนด์ก่อน"); return; }
    setErr(null); setLoading(true); setOut(null); setSaved(false);
    try {
      const num: Record<string, any> = {};
      for (const [k, v] of Object.entries(metrics)) if (v !== "") num[k] = Number(v);
      const r = await api.post<any>("/opinion", { brandId: activeBrand.id, plan: p, metrics: num });
      if (r.error === "ai_unparsable") { setErr("AI ตอบไม่เป็นรูปแบบ ลองใหม่"); return; }
      setOut(r.output); setIsMock(r.isMock); load();
    } catch (e: any) { setErr("ผิดพลาด: " + (e?.message || "")); }
    finally { setLoading(false); }
  }

  async function saveLearning() {
    if (!activeBrand || !out) return;
    await api.post("/learnings", { brandId: activeBrand.id, type: "ads", title: `Second Opinion: ${out.stanceLabel}`, situation: plan, sourceModule: "second-opinion", confidence: out.confidence });
    setSaved(true);
  }

  const meta = out ? STANCE_META[out.stance] : null;

  return (
    <div className="page">
      <div className="page__head mh-spread">
        <div>
          <div className="page__title">ขอ Second Opinion</div>
          <div className="page__subtitle">{activeBrand ? <>บริบท: <b>{activeBrand.name}</b> — AI จะท้าทายแผนคุณ ไม่เออออตาม</> : "เลือกแบรนด์ก่อน"}</div>
        </div>
        <Icon name="Scale" size={22} />
      </div>

      <div className="mh-card card-pad" style={{ marginBottom: 18 }}>
        <label className="mh-label">แผนของคุณ (My Plan)</label>
        <textarea className="mh-textarea" style={{ minHeight: 90 }} value={plan} onChange={(e) => setPlan(e.target.value)}
          placeholder="เช่น: ผมจะปิด Ad Set B แล้วโยกงบไป A เพราะ B แพงกว่า" />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
          {SAMPLES.map((s) => <button key={s} className="mh-btn mh-btn--subtle mh-btn--sm" onClick={() => ask(s)}>{s}</button>)}
        </div>

        <div className="mh-label" style={{ marginTop: 16, marginBottom: 8 }}>ตัวเลขประกอบ (ถ้ามี — ช่วยให้ AI ท้าทายได้แม่นขึ้น)</div>
        <div className="form-grid" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(120px,1fr))" }}>
          {QUICK.map((q) => (
            <div className="mh-field" style={{ margin: 0 }} key={q.key}>
              <label className="mh-label" style={{ fontSize: 12.5 }}>{q.label}</label>
              <input className="mh-input" type="number" value={metrics[q.key] ?? ""} onChange={(e) => setMetrics((p) => ({ ...p, [q.key]: e.target.value }))} />
            </div>
          ))}
        </div>

        <div style={{ marginTop: 16, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <button className="mh-btn mh-btn--primary mh-btn--lg" onClick={() => ask()} disabled={loading}>
            {loading ? <span className="spinner" /> : <><Icon name="Scale" size={18} /> ขอความเห็น</>}
          </button>
          {history.length > 0 && <span className="mh-muted" style={{ fontSize: 13 }}>เคยถามมาแล้ว {history.length} ครั้ง</span>}
          {err && <span className="mh-pill mh-pill--problem">{err}</span>}
        </div>
      </div>

      {loading && <div className="empty"><span className="spinner spinner--dark" /><p className="mh-sec">กำลังพิจารณาแผน...</p></div>}

      {out && meta && (
        <>
          {isMock && <div className="mock-banner"><Icon name="Wrench" size={18} /><div>ความเห็นจาก <b>ตรรกะ Mock</b> — ใส่ Claude API Key เพื่อการวิเคราะห์จริง</div></div>}

          <div className="mh-card card-pad" style={{ marginBottom: 14 }}>
            <div className="mh-spread" style={{ marginBottom: 12 }}>
              <div className="mh-sec" style={{ fontSize: 13.5 }}>{out.understanding}</div>
              <span className={"mh-pill " + meta.pill} style={{ fontSize: 14, padding: "5px 14px", flex: "none" }}>{meta.label}</span>
            </div>
            <button className="mh-btn mh-btn--subtle mh-btn--sm" onClick={saveLearning} disabled={saved}>
              {saved ? "บันทึกแล้ว ✓" : <><Icon name="Save" size={15} /> Save as Learning</>}
            </button>
          </div>

          <div className="mh-card card-pad" style={{ marginBottom: 14, background: "var(--blue-050)", borderColor: "var(--blue-100)" }}>
            <div className="mh-row" style={{ gap: 8, fontWeight: 700, marginBottom: 6 }}><Icon name="Sparkles" size={16} /> ตรวจสมมติฐานของคุณ</div>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {out.challengedAssumptions.map((a, i) => <li key={i} style={{ marginBottom: 6 }}>{a}</li>)}
            </ul>
          </div>

          <div className="form-grid">
            <Panel icon="Sparkles" title="เหตุผล (Reason)">{out.reason}</Panel>
            <Panel icon="Radar" title="ตรวจข้อมูล (Data Check)">{out.dataCheck}</Panel>
            <Panel icon="Stethoscope" title="ความเสี่ยง (Risk)" tone="warn">{out.risk}</Panel>
            <Panel icon="TrendingUp" title="ทางเลือก (Alternative)">{out.alternative}</Panel>
          </div>

          <div className="mh-card card-pad" style={{ marginTop: 14, borderLeft: "4px solid var(--blue-600)" }}>
            <div className="mh-row" style={{ gap: 8, fontWeight: 700, marginBottom: 6 }}><Icon name="Scale" size={16} /> การตัดสินใจที่แนะนำ</div>
            <div style={{ fontSize: 15.5 }}>{out.recommendedDecision}</div>
          </div>
        </>
      )}
    </div>
  );
}

function Panel({ title, icon, children, tone }: { title: string; icon: string; children: React.ReactNode; tone?: "warn" }) {
  return (
    <div className="mh-card card-pad" style={tone === "warn" ? { background: "var(--amber-050)", borderColor: "#f2dca0" } : undefined}>
      <div className="mh-row" style={{ gap: 8, marginBottom: 8, fontWeight: 700, fontSize: 14 }}><Icon name={icon} size={16} /> {title}</div>
      <div style={{ fontSize: 14, lineHeight: 1.6 }}>{children}</div>
    </div>
  );
}
